import React from 'react';

import {
	FASTSHIFT_ROLL_UP_STATUS,
	FastshiftRelatedTicketIssueType,
	FastshiftRollUpStatus,
} from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { FormattedMessage, useIntl } from 'react-intl';

import { LinkIconButton } from '@atlaskit/button/new';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import Lozenge from '@atlaskit/lozenge';
import { Stack, Box, Text, xcss, Inline } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { SAC_URL } from '@/common/routes/constants';
import { getStatusColor, getStatusText } from '@/common/utils';

import { messages } from './messages';
import { getMigrationTicketIcon } from './utils';

type MigrationTicketsCardProps = {
	issueType: FastshiftRelatedTicketIssueType;
	supportTicketId: string;
	title: string;
	status: FastshiftRollUpStatus;
	showBottomBorder?: boolean;
	compact?: boolean;
	onClickAnalyticsEvent: (ticketId: string) => void;
};

const listItemStyles = xcss({
	paddingBlock: 'space.150',
	paddingInline: 'space.200',
});

const notFirstItemStyles = xcss({
	borderBlockStart: `1px solid ${token('color.border')}`,
});

const iconWrapperStyles = xcss({
	width: '32px',
	height: '32px',
	borderRadius: 'radius.small',
	padding: 'space.050',
	border: `1px solid ${token('color.border')}`,
	marginInlineEnd: 'space.200',
	flexShrink: 0,
});

const iconCompactWrapperStyles = xcss({
	alignSelf: 'flex-start',
});

const linkIconStyles = xcss({
	marginInlineStart: 'space.500',
});

const compactLinkIconStyles = xcss({
	marginBlockStart: 'space.200',
});

const statusWrapperStyles = xcss({
	marginBlockStart: 'space.100',
});

const strikeThroughTextStyles = xcss({
	textDecoration: 'line-through',
});

export const MigrationTicketsCard: React.FC<MigrationTicketsCardProps> = ({
	issueType,
	supportTicketId,
	title,
	status,
	showBottomBorder = false,
	compact,
	onClickAnalyticsEvent,
}) => {
	const intl = useIntl();

	return (
		<Box
			testId={`migration-ticket-list-item-${supportTicketId}`}
			xcss={[listItemStyles, showBottomBorder && notFirstItemStyles]}
		>
			<Inline spread="space-between" alignBlock="center">
				<Inline alignBlock="center">
					<Stack
						xcss={[iconWrapperStyles, compact && iconCompactWrapperStyles]}
						alignBlock="center"
						alignInline="center"
						grow="hug"
					>
						{getMigrationTicketIcon(issueType)}
					</Stack>
					<Stack>
						<Tooltip content={title}>
							<Text size="medium" maxLines={2}>
								{title}
							</Text>
						</Tooltip>

						<Text size="small" color="color.text.subtle">
							<Box
								xcss={status === FASTSHIFT_ROLL_UP_STATUS.COMPLETE && strikeThroughTextStyles}
								as="span"
							>
								{supportTicketId}
							</Box>
						</Text>
						{compact && (
							<Inline xcss={statusWrapperStyles}>
								<Lozenge appearance={getStatusColor(status)}>
									<FormattedMessage {...getStatusText(status)} />
								</Lozenge>
							</Inline>
						)}
					</Stack>
				</Inline>
				<Inline alignBlock="center">
					{!compact && (
						<Lozenge appearance={getStatusColor(status)}>
							<FormattedMessage {...getStatusText(status)} />
						</Lozenge>
					)}
					<Stack
						xcss={compact ? compactLinkIconStyles : linkIconStyles}
						alignBlock="center"
						alignInline="center"
					>
						<LinkIconButton
							href={`${SAC_URL}/requests/${supportTicketId}`}
							target="_blank"
							appearance="subtle"
							icon={LinkExternalIcon}
							label={intl.formatMessage(messages.iconLinkAriaLabel)}
							onClick={() => onClickAnalyticsEvent(supportTicketId)}
						/>
					</Stack>
				</Inline>
			</Inline>
		</Box>
	);
};
